import React, { useEffect, useState } from 'react';
import { Upload, Button, Table, Typography, Collapse, message } from 'antd';
import { UploadOutlined, DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { OpPage } from 'components/customAntd/OpPage/OpPage';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInvitationConfig } from 'store/slices/visitorInvitationSlice';
import { ConfigItem, InvitationConfig } from 'types/visitorInvitationTypes';
import dayjs from 'dayjs';
import Papa from 'papaparse';
import { DATE_TIME_FORMAT } from 'constants/dates';
import { formatFullName, hasPermission } from 'utils/utils';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { fetchLocations } from 'store/slices/locationsSlice';
import { getRequest, postRequest } from 'api/apiClient';
import { User } from 'types/userTypes';
import { VisitorType } from 'types/VisitorTypeTypes';
import STATUS from 'constants/status';
import { useTranslation } from 'react-i18next';
import { fetchVisits, setVisitsEndDate, setVisitsStartDate } from 'store/slices/visitsSlice';
import i18n from 'i18n/i18n';

const { Title } = Typography;
const { Panel } = Collapse;

const sampleCsvUrl = '/sample/bulk-schedule-sample.csv';

const BulkSchedule: React.FC = () => {
    const navigate = useNavigate();

    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);

    const invitationConfig = useSelector((state: RootState) => state.visitorInvitation.invitationConfig);
    const locations = useSelector((state: RootState) => state.locations.locations);

    const [fileList, setFileList] = useState<any[]>([]);
    const [uploadStatus, setUploadStatus] = useState<JSX.Element[] | null>(null);
    const [validEntries, setValidEntries] = useState<any[]>([]);

    const [visitorTypes, setVisitorTypes] = useState<VisitorType[]>([]);
    const [hosts, setHosts] = useState<User[]>([]);

    const hasVisitorsBulkWrite = hasPermission(tokenScopeList, orgId, 'o', 'visitorsbulk:w');

    const confirmModal = useConfirmModal();

    const { t } = useTranslation();

    useEffect(() => {
        const fetchData = async () => {
            if (orgId) {
                if (!invitationConfig) {
                    dispatch(fetchInvitationConfig({ orgId }));
                }
                if (locations.length === 0) {
                    dispatch(fetchLocations({ orgId, status: 1 }));
                }

                try {
                    const hosts = await getRequest(`/orgs/${orgId}/users`);
                    const filteredHosts = hosts.data.filter((user: User) =>
                        user.status === 1 && (user.hostExclude === 0 || user.hostExclude === null)
                    );
                    setHosts(filteredHosts);

                    const visitorTypes = await getRequest(`/orgs/${orgId}/visitorTypes`);
                    setVisitorTypes(visitorTypes.data);
                } catch (error) {
                    console.error("Failed to fetch:", error);
                }
            }
        };
        fetchData();
    }, [dispatch, orgId, invitationConfig, locations.length]);

    const columns = [
        {
            title: t('bulkSchedule.fields.fieldName'),
            dataIndex: 'field',
            key: 'field',
        },
        {
            title: t('bulkSchedule.fields.required'),
            dataIndex: 'required',
            key: 'required',
        },
        {
            title: t('bulkSchedule.fields.acceptableValues'),
            dataIndex: 'acceptable',
            key: 'acceptable',
        },
        {
            title: t('bulkSchedule.fields.description'),
            dataIndex: 'description',
            key: 'description',
        },
    ];

    // Map: field key -> label & example
    const FIELD_INFO: Record<string, { label: string, example: string, description: string, configKey?: keyof InvitationConfig }> = {
        "Location": {
            label: t('bulkSchedule.fieldInfo.location.label'),
            example: t('bulkSchedule.fieldInfo.location.example'),
            description: t('bulkSchedule.fieldInfo.location.description')
        },
        "First Name": {
            label: t('bulkSchedule.fieldInfo.firstName.label'),
            example: t('bulkSchedule.fieldInfo.firstName.example'),
            description: t('bulkSchedule.fieldInfo.firstName.description'),
            configKey: "firstName"
        },
        "Last Name": {
            label: t('bulkSchedule.fieldInfo.lastName.label'),
            example: t('bulkSchedule.fieldInfo.lastName.example'),
            description: t('bulkSchedule.fieldInfo.lastName.description'),
            configKey: "lastName"
        },
        "Email": {
            label: t('bulkSchedule.fieldInfo.email.label'),
            example: t('bulkSchedule.fieldInfo.email.example'),
            description: t('bulkSchedule.fieldInfo.email.description'),
            configKey: "email"
        },
        "Visit Phone": {
            label: t('bulkSchedule.fieldInfo.visitPhone.label'),
            example: t('bulkSchedule.fieldInfo.visitPhone.example'),
            description: t('bulkSchedule.fieldInfo.visitPhone.description'),
            configKey: "visitPhone"
        },
        "Visitor Type": {
            label: t('bulkSchedule.fieldInfo.visitorType.label'),
            example: t('bulkSchedule.fieldInfo.visitorType.example'),
            description: t('bulkSchedule.fieldInfo.visitorType.description')
        },
        "Start Time": {
            label: t('bulkSchedule.fieldInfo.startTime.label'),
            example: t('bulkSchedule.fieldInfo.startTime.example'),
            description: t('bulkSchedule.fieldInfo.startTime.description')
        },
        "End Time": {
            label: t('bulkSchedule.fieldInfo.endTime.label'),
            example: t('bulkSchedule.fieldInfo.endTime.example'),
            description: t('bulkSchedule.fieldInfo.endTime.description')
        },
        "Visit Company": {
            label: t('bulkSchedule.fieldInfo.visitCompany.label'),
            example: t('bulkSchedule.fieldInfo.visitCompany.example'),
            description: t('bulkSchedule.fieldInfo.visitCompany.description'),
            configKey: "visitCompany"
        },
        "Host": {
            label: t('bulkSchedule.fieldInfo.host.label'),
            example: t('bulkSchedule.fieldInfo.host.example'),
            description: t('bulkSchedule.fieldInfo.host.description'),
            configKey: "host"
        },
        "Visit Purpose": {
            label: t('bulkSchedule.fieldInfo.visitPurpose.label'),
            example: t('bulkSchedule.fieldInfo.visitPurpose.example'),
            description: t('bulkSchedule.fieldInfo.visitPurpose.description'),
            configKey: "visitPurpose"
        },
        "Visit Note": {
            label: t('bulkSchedule.fieldInfo.visitNote.label'),
            example: t('bulkSchedule.fieldInfo.visitNote.example'),
            description: t('bulkSchedule.fieldInfo.visitNote.description'),
            configKey: "visitNotes"
        },
    };

    const ALWAYS_INCLUDED = ["Location", "Visitor Type", "Start Time", "End Time"];

    const parseFlexibleDate = (value: string): dayjs.Dayjs | null => {
        if (!value) return null;

        const cleaned = value.trim().replace(/\u00A0/g, '');
        const formats = [
            'YYYY-MM-DD h:mm A',
            'YYYY-MM-DD hh:mm A',
            'YYYY-MM-DD HH:mm:ss',
            'YYYY-MM-DD h:mm',
            'YYYY-MM-DD HH:mm',
            'MM/DD/YY h:mm A',
            'MM/DD/YYYY h:mm A',
            'MM/DD/YY hh:mm A',
            'MM/DD/YYYY hh:mm A',
            'MM/DD/YYYY h:mm',
            'MM/DD/YYYY HH:mm',
            'M/D/YY H:mm',
            'M/D/YY h:mm A',
            'M/D/YYYY H:mm',
            'M/D/YYYY h:mm A',
            'MM/DD/YY H:mm',
            'MM/DD/YYYY H:mm'
        ];

        for (const fmt of formats) {
            const parsed = dayjs(cleaned, fmt, true);
            if (parsed.isValid()) return parsed;
        }
        return null;
    };

    const getIncludedFields = (invitationConfig: InvitationConfig | null | undefined): string[] => {
        if (!invitationConfig) return ALWAYS_INCLUDED;

        return Object.entries(FIELD_INFO)
            .filter(([key, info]) =>
                ALWAYS_INCLUDED.includes(key) ||
                (
                    info.configKey &&
                    (invitationConfig[info.configKey] as ConfigItem)?.included === 1
                )
            )
            .map(([key]) => key);
    };

    const INCLUDED_HEADERS = getIncludedFields(invitationConfig);

    const data = Object.entries(FIELD_INFO).map(([field, info]) => {
        const isAlwaysRequired = ALWAYS_INCLUDED.includes(field);
        const isRequired =
            isAlwaysRequired ||
            (
                info.configKey && invitationConfig &&
                (invitationConfig[info.configKey] as ConfigItem)?.required === 1
            );

        return {
            field: info.label,
            required: isRequired ? t("Yes") : t("No"),
            acceptable: `${t('E.g.')} ${info.example}`,
            description: info.description
        };
    });

    const handleUpload = async () => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;

            const parsed = Papa.parse<Record<string, string>>(text, { header: true });
            const actualHeaders = parsed.meta.fields || [];

            const missingHeaders = INCLUDED_HEADERS.filter(h => !actualHeaders.includes(h));

            if (missingHeaders.length > 0) {
                const statusLines: JSX.Element[] = [];

                statusLines.push(<div><strong>{t('bulkSchedule.uploadErrors.invalidFormat')}</strong></div>);

                if (missingHeaders.length > 0) {
                    statusLines.push(
                        <div style={{ color: 'red', marginTop: 8 }}>
                            {t('bulkSchedule.uploadErrors.missingHeaders', { headers: missingHeaders.join(', ') })}
                        </div>
                    );
                }
                setUploadStatus(statusLines);
                return;
            }
            const seenKeys = new Set<string>();
            const uniqueRows = parsed.data.filter((row) => {
                const start = parseFlexibleDate(row['Start Time'])?.format('YYYY-MM-DD HH:mm');
                const end = parseFlexibleDate(row['End Time'])?.format('YYYY-MM-DD HH:mm');
                const key = [
                    row['First Name']?.trim().toLowerCase(),
                    row['Last Name']?.trim().toLowerCase(),
                    row['Email']?.trim().toLowerCase(),
                    start,
                    end
                ].join('|');

                if (seenKeys.has(key)) return false;
                seenKeys.add(key);
                return true;
            });

            const deduplicatedCsv = Papa.unparse(uniqueRows);

            console.log('deduplicatedCsv', deduplicatedCsv);
            const { errors, validEntries, totalEntries } = validateCsv(deduplicatedCsv, invitationConfig!, fileList[0].name);
            setValidEntries(validEntries);

            const statusLines: JSX.Element[] = [];
            if (errors.length > 0) {
                statusLines.push(<div>{t('bulkSchedule.uploadErrors.successfulUpload', { valid: validEntries.length, total: totalEntries })}</div>);
                statusLines.push(<br />);
                statusLines.push(<strong>{t('bulkSchedule.uploadErrors.errorsTitle')}</strong>);

                errors.forEach((err, i) => {
                    statusLines.push(
                        <div key={i} style={{ margin: '8px 0' }}>
                            {err.split('\n').map((line, idx) =>
                                idx === 0 ? (
                                    <div key={idx}>{line}</div>
                                ) : (
                                    <div key={idx} style={{ color: 'red' }}>{line.trim()}</div>
                                )
                            )}
                        </div>
                    );
                });

                statusLines.push(
                    <div style={{ marginTop: 12 }}>
                        {t('bulkSchedule.uploadErrors.importInstruction', { count: validEntries.length })}
                    </div>
                );
            } else {
                statusLines.push(<div>{t('bulkSchedule.uploadErrors.successfulUpload', { valid: totalEntries, total: totalEntries })}</div>);
            }

            setUploadStatus(statusLines);
        };
        reader.readAsText(fileList[0].originFileObj);
        message.success(t('bulkSchedule.uploadErrors.scheduleSuccess'));
    };

    const validateCsv = (csvText: string, invitationConfig: InvitationConfig, fileName: string) => {
        const results = Papa.parse<Record<string, string>>(csvText, { header: true });
        const errors: string[] = [];
        const validEntries: any[] = [];

        const hostNames = hosts.map(host => formatFullName(host.identity.firstName || '', host.identity.middleName || '', host.identity.lastName || ''));

        const isRequired = (field: keyof InvitationConfig) => {
            const config = invitationConfig[field];
            if (typeof config === 'object' && config !== null && 'included' in config && 'required' in config) {
                return config.included === 1 && config.required === 1;
            }
            return false;
        };

        const rows = results.data.filter((row) =>
            Object.values(row).some((cell) => {
                return typeof cell === 'string' && cell.trim() !== '';
            })
        );

        rows.forEach((row: any, index: number) => {
            const rowErrors: string[] = [];

            const fullName = formatFullName(row['First Name'] || '', '', row['Last Name'] || '') || '(Unknown)';

            const isEmpty = (value: string | null | undefined) =>
                !value || value.trim() === '';

            console.log('row', row);
            if (isEmpty(row['Location'])) rowErrors.push(t('bulkSchedule.rowErrors.missingLocation'));
            else if (!locations.map(loc => loc.name.toLowerCase()).includes(row['Location'].trim().toLowerCase())) {
                rowErrors.push(t('bulkSchedule.rowErrors.invalidLocation', { location: row['Location'] }));
            }

            if (isEmpty(row['First Name'])) rowErrors.push(t('bulkSchedule.rowErrors.missingFirstName'));
            if (isEmpty(row['Last Name'])) rowErrors.push(t('bulkSchedule.rowErrors.missingLastName'));
            if (isRequired('visitPhone') && isEmpty(row['Visit Phone'])) rowErrors.push(t('bulkSchedule.rowErrors.missingVisitPhone'));


            if (isRequired('email') && isEmpty(row['Email'])) rowErrors.push(t('bulkSchedule.rowErrors.missingEmail'));
            if (row['Email'] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row['Email'].trim())) {
                rowErrors.push(t('bulkSchedule.rowErrors.invalidEmail'));
            }

            if (isEmpty(row['Visitor Type'])) {
                rowErrors.push(t('bulkSchedule.rowErrors.missingVisitorType'));
            } else if (!visitorTypes.map(vt => vt.name?.toLowerCase()).includes(row['Visitor Type'].trim().toLowerCase())) {
                rowErrors.push(t('bulkSchedule.rowErrors.invalidVisitorType', { visitorType: row['Visitor Type'] }));
            }

            const startTimeStr = row['Start Time']?.trim();
            const endTimeStr = row['End Time']?.trim();

            const startTime = parseFlexibleDate(startTimeStr);
            const endTime = parseFlexibleDate(endTimeStr);

            if (!startTime) rowErrors.push(t('bulkSchedule.rowErrors.invalidStartTime'));
            else if (dayjs(startTime).isBefore(dayjs())) rowErrors.push(t('bulkSchedule.rowErrors.startTimePast'));

            if (!endTime) rowErrors.push(t('bulkSchedule.rowErrors.invalidEndTime'));
            else if (startTime && !startTime.isBefore(endTime)) rowErrors.push(t('bulkSchedule.rowErrors.startTimeBeforeEndTime'));

            if (isRequired('host') && isEmpty(row['Host'])) rowErrors.push(t('bulkSchedule.rowErrors.missingHost'));
            if (row['Host'] && !hostNames.map(h => h.toLowerCase()).includes(row['Host'].trim().toLowerCase())) {
                rowErrors.push(t('bulkSchedule.rowErrors.invalidHost', { host: row['Host'] }));
            }

            if (isRequired('visitCompany') && isEmpty(row['Visit Company'])) {
                rowErrors.push(t('bulkSchedule.rowErrors.missingCompany'));
            }
            if (isRequired('visitPurpose') && isEmpty(row['Visit Purpose'])) {
                rowErrors.push(t('bulkSchedule.rowErrors.missingPurpose'));
            }
            if (isRequired('visitNotes') && isEmpty(row['Visit Note'])) {
                rowErrors.push(t('bulkSchedule.rowErrors.missingNote'));
            }

            if (rowErrors.length > 0) {
                errors.push(
                    t('bulkSchedule.errorSummary', { fullName, fileName }) + '\n' +
                    `  ${rowErrors.join(', ')}`
                );
            } else {
                validEntries.push(row);
            }
        });

        return { errors, validEntries, totalEntries: rows.length };
    };

    const handleImport = async () => {
        try {
            const invitationPayloads = validEntries.map((row: any) => {
                const start = dayjs(row['Start Time']).format(DATE_TIME_FORMAT);
                const end = dayjs(row['End Time']).format(DATE_TIME_FORMAT);

                const host = hosts.find(h =>
                    formatFullName(h.identity.firstName || '', h.identity.middleName || '', h.identity.lastName || '').toLowerCase().trim() === row['Host'].trim().toLowerCase()
                );

                const location = locations.find(loc => loc.name.toLowerCase() === row['Location'].trim().toLowerCase());

                const visitorType = visitorTypes.find(vt => vt.name?.toLowerCase() === row['Visitor Type'].trim().toLowerCase());

                return {
                    siteId: location?.id,
                    visitorTypeId: visitorType?.id,
                    company: invitationConfig?.visitCompany.included === 1 ? (row['Visit Company']?.trim() || null) : null,
                    scheduleIn: start,
                    scheduleOut: end,
                    hostId: invitationConfig?.host.included === 1 ? host?.id : null,
                    purpose: invitationConfig?.visitPurpose.included === 1 ? (row['Visit Purpose']?.trim() || null) : null,
                    note: invitationConfig?.visitNotes.included === 1 ? (row['Visit Note']?.trim() || null) : null,
                    visitors: [
                        {
                            firstName: invitationConfig?.firstName.included === 1 ? (row['First Name']?.trim() || null) : null,
                            middleName: null,
                            lastName: invitationConfig?.lastName.included === 1 ? (row['Last Name']?.trim() || null) : null,
                            email: invitationConfig?.email.included === 1 ? (row['Email']?.trim() || null) : null,
                            mobilePhone: invitationConfig?.visitPhone.included === 1 ? (row['Visit Phone']?.trim() || null) : null,
                            userId: null,
                            status: STATUS.PENDING.id,
                            signIn: null,
                            signOut: null,
                        }
                    ]
                };
            });

            for (const payload of invitationPayloads) {
                await postRequest(`/orgs/${orgId}/visitorInvitation?language=${i18n.language.split('-')[0]}`, payload);
            }

            message.success(t('bulkSchedule.uploadErrors.scheduleSuccess'));

            await dispatch(setVisitsStartDate(dayjs().startOf('day').format(DATE_TIME_FORMAT)));
            await dispatch(setVisitsEndDate(dayjs().endOf('day').format(DATE_TIME_FORMAT)));
            await dispatch(fetchVisits({ orgId }));

            navigate('/visitors');
        } catch (error) {
            console.error('Error while scheduling invitations:', error);
            message.error(t('bulkSchedule.uploadErrors.scheduleError'));
        }
    };

    return (
        <OpPage
            title={t('bulkSchedule.title')}
            tooltip={t('bulkSchedule.tooltip')}
        >
            <Typography.Paragraph>{t('bulkSchedule.description1')}</Typography.Paragraph>
            <Typography.Paragraph>{t('bulkSchedule.description2')}</Typography.Paragraph>

            <Collapse defaultActiveKey={['1']}>
                <Panel header={t('bulkSchedule.validFields')} key="1">
                    <Table columns={columns} dataSource={data} pagination={false} rowKey="field" scroll={{ x: 'max-content' }} />
                </Panel>
            </Collapse>

            {(hasVisitorsBulkWrite) ? (
                <div style={{ marginTop: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Upload
                            beforeUpload={() => false}
                            onChange={({ fileList }) => setFileList(fileList)}
                            showUploadList={false}
                            fileList={fileList}
                            accept=".csv"
                            maxCount={1}
                        >
                            <Button icon={<UploadOutlined />}>{t('bulkSchedule.selectCsv')}</Button>
                        </Upload>

                        {fileList.length > 0 && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: 'var(--colorInfoBgHover)',
                                    padding: '0px 8px',
                                    borderRadius: 4,
                                }}
                            >
                                <span>{fileList[0].name}</span>
                                <Button
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                        setFileList([]);
                                        setUploadStatus(null);
                                    }}
                                    style={{ marginLeft: 8 }}
                                />
                            </div>
                        )}
                    </div>


                    <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
                        <Button
                            type="primary"
                            onClick={() => {
                                confirmModal({
                                    title: t('bulkSchedule.uploadConfirmationTitle'),
                                    content: t('bulkSchedule.uploadConfirmationContent'),
                                    okText: t('bulkSchedule.ok'),
                                    cancelText: t('bulkSchedule.cancel'),
                                    onOk: handleUpload,
                                });
                            }}
                            disabled={!fileList.length}
                        >
                            {t('bulkSchedule.selectCsv')}
                        </Button>

                        <Button
                            icon={<DownloadOutlined />}
                            href={sampleCsvUrl}
                            download="bulk-schedule-sample.csv"
                        >
                            {t('bulkSchedule.downloadSampleCsv')}
                        </Button>
                    </div>

                    <div style={{ marginTop: 24 }}>
                        <Title level={4}>{t('bulkSchedule.uploadStatusTitle')}</Title>
                        <div style={{
                            minHeight: 200, background: ('var(--colorInfoBg)'), padding: 16, border: '1px solid var(--colorBorder)', borderRadius: 8, whiteSpace: 'pre-wrap', height: 300, overflowY: 'auto',
                        }}>
                            {uploadStatus || t('bulkSchedule.noFileUploaded')}
                        </div>
                    </div>

                    <div style={{ marginTop: 24 }}>
                        <Button
                            type="primary"
                            onClick={() => {
                                confirmModal({
                                    title: t('bulkSchedule.importConfirmationTitle'),
                                    content: t('bulkSchedule.importConfirmationContent'),
                                    okText: t('bulkSchedule.import'),
                                    cancelText: t('bulkSchedule.cancel'),
                                    onOk: handleImport,
                                });
                            }}
                            disabled={!uploadStatus}
                        >
                            {t('bulkSchedule.import')}
                        </Button>
                    </div>
                </div>
            ) : (
                <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
                    <Button
                        icon={<DownloadOutlined />}
                        href={sampleCsvUrl}
                        download="bulk-schedule-sample.csv"
                    >
                        {t('bulkSchedule.downloadSampleCsv')}
                    </Button>
                </div>
            )}

        </OpPage>
    );
};

export default BulkSchedule;
